import { useTranslation } from 'react-i18next'
import { PerModal } from '../../../components/custom_components/Modal'
import PerText from '../../../components/custom_components/Text'
import { FC, useEffect, useState } from 'react'
import {
  Country,
  EmployeeEmploymentStatusDto,
  EmployeeHasOngoingAppraisalQuery,
  EmploymentStatusOfEmployeeQuery,
  TalentsSeasonSettingsDto,
  TalentsSeasonStatus,
} from '../../../types'
import { ApolloQueryResult } from '@apollo/client'
import { MaybeMasked } from '@apollo/client'
import { useCultivatingTalentsStore } from '../../cultivatingTalents/CultivatingTalentsStore'
import useHandleEmployeeTags from '../../../graphql/ranking/hooks/useHandleEmployeeTag'
import shallow from 'zustand/shallow'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import { Spin } from 'antd'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import { StatusMessageBox } from '../../../components/custom_components/StatusMessageBox'

type AddEntryLevelLowTagAssignmentModalProps = {
  onClose: () => void
  employeeFullName: string
  employeeFirstName: string
  employeeLastName: string
  employeeHasFutureEmploymentChange: (employeeId: string) => Promise<ApolloQueryResult<MaybeMasked<EmploymentStatusOfEmployeeQuery>>>
  employeeHasOngoingAppraisal: (employeeId: string) => Promise<ApolloQueryResult<MaybeMasked<EmployeeHasOngoingAppraisalQuery>>>
  companyId: string
  country: Country
  employeeId: string
}

export const AddEntryLevelLowTagAssignmentModal: FC<AddEntryLevelLowTagAssignmentModalProps> = ({
  onClose,
  employeeFullName,
  employeeFirstName,
  employeeLastName,
  employeeHasFutureEmploymentChange,
  employeeHasOngoingAppraisal,
  companyId,
  country,
  employeeId,
}) => {
  const [t] = useTranslation()
  const [modalError, setModalError] = useState<string | undefined>(undefined)
  const [isLoading, toggleLoading] = useState(false)
  const [isProcessing, toggleProcessing] = useState(false)
  const [isContentLoaded, setIsContentLoaded] = useState(false)
  const [ongoingAppraisal, toggleOngoingAppraisal] = useState(false)
  const [futureEmploymentChanges, toggleFutureEmploymentChanges] = useState<EmployeeEmploymentStatusDto | undefined>(undefined)

  const [talentsSeasonSettings] = useCultivatingTalentsStore((state) => [state.talentsSeasonSettings], shallow)
  const referenceYear = talentsSeasonSettings ? talentsSeasonSettings.year : new Date().getUTCFullYear()
  const rankingInProgress = talentsSeasonSettings
    ? [TalentsSeasonStatus.Opened, TalentsSeasonStatus.Confirmed].includes(talentsSeasonSettings.talentsSeasonStatus)
    : false

  const { addEntryLevelLowTag } = useHandleEmployeeTags(companyId, country, referenceYear, rankingInProgress)

  const loadErrorMessage = t('rankingView.table.actions.tagEmployeePerformance.addEntryLevelLowTagAssignmentModal.loadError')
  const confirmErrorMessage = t('rankingView.table.actions.tagEmployeePerformance.addEntryLevelLowTagAssignmentModal.confirmError')

  const resetModal = () => {
    setIsContentLoaded(false)
    setModalError(undefined)
    toggleFutureEmploymentChanges(undefined)
    toggleOngoingAppraisal(false)
    toggleLoading(false)
  }

  const handleConfirmLowTagAssignmentModalCancel = () => {
    onClose()
    resetModal()
  }

  const handleConfirmLowTagAssignmentModalConfirm = async () => {
    toggleProcessing(true)
    setModalError(undefined)

    const { errors } = await addEntryLevelLowTag(employeeId)

    toggleProcessing(false)

    if (errors?.length) {
      setModalError(errors[0]?.extensions?.validationErrors?.[0]?.message || confirmErrorMessage)
      return
    }

    onClose()
    resetModal()

    showNotification({
      type: 'success',
      message: t('rankingView.table.actions.tagEmployeePerformance.notifications.update.title'),
      description: t('rankingView.table.actions.tagEmployeePerformance.notifications.update.description'),
    })
  }

  useEffect(() => {
    let cancelled = false

    const loadModalContent = async () => {
      toggleLoading(true)
      let hasOngoingAppraisal = false
      let hasFutureChanges: EmployeeEmploymentStatusDto | undefined = undefined

      try {
        const res = await employeeHasFutureEmploymentChange(employeeId)
        hasFutureChanges = res.data.employmentStatusOfEmployee
      } catch {
        if (!cancelled) {
          setModalError(loadErrorMessage)
          toggleLoading(false)
        }
        return
      }

      if (hasFutureChanges.futureChangesForeseenByContract && !hasFutureChanges.openChanges) {
        try {
          const res = await employeeHasOngoingAppraisal(employeeId)
          hasOngoingAppraisal = res.data.employeeHasOngoingAppraisal
        } catch {
          if (!cancelled) {
            setModalError(loadErrorMessage)
            toggleLoading(false)
          }
          return
        }
      }

      if (!cancelled) {
        toggleFutureEmploymentChanges(hasFutureChanges)
        toggleOngoingAppraisal(hasOngoingAppraisal)
        toggleLoading(false)
        setIsContentLoaded(true)
      }
    }

    void loadModalContent()

    return () => {
      cancelled = true
    }
  }, [employeeId, talentsSeasonSettings, employeeHasFutureEmploymentChange, employeeHasOngoingAppraisal, loadErrorMessage])

  return (
    <PerModal
      zIndex={2000}
      centered
      visible
      onCancel={handleConfirmLowTagAssignmentModalCancel}
      title={{
        heading: t('rankingView.table.actions.tagEmployeePerformance.addEntryLevelLowTagAssignmentModal.heading'),
      }}
      footer={{
        confirmBtn: {
          onClick: handleConfirmLowTagAssignmentModalConfirm,
          disabled:
            !!modalError ||
            isLoading ||
            isProcessing ||
            !futureEmploymentChanges ||
            !futureEmploymentChanges.futureChangesForeseenByContract,
          isProcessing: { active: isProcessing },
        },
        cancelBtn: { onClick: handleConfirmLowTagAssignmentModalCancel },
      }}
    >
      {isContentLoaded && (
        <AddEntryLevelLowTagAssignmentModalContent
          employeeFullName={employeeFullName}
          employeeFirstName={employeeFirstName}
          employeeLastName={employeeLastName}
          ongoingAppraisal={ongoingAppraisal}
          futureEmploymentChanges={futureEmploymentChanges!}
          talentsSeasonSettings={talentsSeasonSettings}
        />
      )}
      {(!!modalError || isLoading) && (
        <>
          {isLoading ? (
            <BackgroundBox color="transparent" noShadow height="messageBox">
              <PerFlexRow justify="center" align="middle" className="fullHeight">
                <Spin size="large" spinning />
              </PerFlexRow>
            </BackgroundBox>
          ) : (
            <StatusMessageBox message={modalError!} variant="error" withTopMargin={isContentLoaded} />
          )}
        </>
      )}
    </PerModal>
  )
}

type AddEntryLevelLowTagAssignmentModalContentProps = {
  employeeFullName: string
  employeeFirstName: string
  employeeLastName: string
  futureEmploymentChanges: EmployeeEmploymentStatusDto
  ongoingAppraisal: boolean
  talentsSeasonSettings: TalentsSeasonSettingsDto | undefined
}

export const AddEntryLevelLowTagAssignmentModalContent: FC<AddEntryLevelLowTagAssignmentModalContentProps> = ({
  employeeFullName,
  employeeFirstName,
  employeeLastName,
  futureEmploymentChanges,
  ongoingAppraisal,
  talentsSeasonSettings,
}) => {
  const [t] = useTranslation()

  return (
    <>
      {!futureEmploymentChanges.futureChangesForeseenByContract ? (
        <StatusMessageBox
          variant="warning"
          message={t(
            'rankingView.table.actions.tagEmployeePerformance.addEntryLevelLowTagAssignmentModal.noExistingFutureChangesForeseenByContract',
            {
              employeeFirstName,
            },
          )}
        />
      ) : talentsSeasonSettings && talentsSeasonSettings.blockedChangesForLow ? (
        <>
          <PerText>
            {futureEmploymentChanges.openChanges
              ? t('rankingView.table.actions.tagEmployeePerformance.addEntryLevelLowTagAssignmentModal.existingOpenEmploymentChanges', {
                  employeeFirstName,
                })
              : ongoingAppraisal
              ? t('rankingView.table.actions.tagEmployeePerformance.addEntryLevelLowTagAssignmentModal.description1OngoingAppraisal', {
                  employeeFirstName,
                  employeeLastName,
                })
              : t('rankingView.table.actions.tagEmployeePerformance.addEntryLevelLowTagAssignmentModal.description1', {
                  employeeFullName,
                })}
          </PerText>

          <PerText>
            {t('rankingView.table.actions.tagEmployeePerformance.addEntryLevelLowTagAssignmentModal.description2', {
              employeeFirstName,
            })}
          </PerText>
          <ul>
            <li>
              <PerText>{t('rankingView.table.actions.tagEmployeePerformance.addEntryLevelLowTagAssignmentModal.item1')}</PerText>
            </li>
            <li>
              <PerText>{t('rankingView.table.actions.tagEmployeePerformance.addEntryLevelLowTagAssignmentModal.item2')}</PerText>
            </li>
          </ul>
        </>
      ) : (
        <>
          <PerText>
            {t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.description1', {
              employeeFullName,
            })}
          </PerText>
        </>
      )}
    </>
  )
}
